import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { color, spacing, Typography, typography } from '@uniquegood/realworld-studio-design';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import SharedNavBar from '@/components/SharedNavBar';
import PricingCard from './components/pricingCard';
import PricingToggle from './components/pricingToggle';
import FAQSection from './components/faqSection';
import CTASection from './components/ctaSection';
import ComparisonTable from './components/comparisonTable';
import ComparisonToggle from './components/comparisonToggle';
import CasesSection from './components/casesSection';
import { BillingPeriod } from './types';
import { pricingData } from './data/pricing';
import { getAccessToken } from '@/auth';
import { OAUTH_LOGIN_HOST } from '@/constants/common';
import Footer from '@/components/SharedFooter';
import { ClickEventName, PageViewEventName, track } from '@/track';

const useScrollAnimation = () => {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return { ref, isVisible };
};

const Pricing = () => {
  const { header, plans, comparison, faq, cta } = pricingData;
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [showComparison, setShowComparison] = useState(false);
  const history = useHistory();

  const headerAnimation = useScrollAnimation();
  const plansAnimation = useScrollAnimation();
  const comparisonAnimation = useScrollAnimation();
  const faqAnimation = useScrollAnimation();
  const ctaAnimation = useScrollAnimation();
  useEffect(() => {
    track.onPageView({ pageViewEventName: PageViewEventName.view_landing_plan });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCtaClick = async (planId: string, billingPeriod?: BillingPeriod) => {
    const accessToken = await getAccessToken();

    if (planId === 'free') {
      track.onClick({
        clickEventName: ClickEventName.click_landing_plan_card_free_start,
        params: { is_logged_in: accessToken ? 'true' : 'false' }
      });
      // 로그인 상태 확인
      if (accessToken) {
        // 이미 로그인되어 있으면 /apps로 이동
        history.push('/apps');
      } else {
        // 로그인 안 되어 있으면 OAuth 서버로 리다이렉트 (로그인 완료 후 /auth/login?redirect=/apps로 돌아옴)
        const loginUrl = `${window.location.origin}/auth/login?redirect=/apps`;
        const oauthLoginUri = `https://${OAUTH_LOGIN_HOST}/auth/signin?redirectUrl=${encodeURIComponent(
          loginUrl
        )}&referrer=studio`;
        window.location.href = oauthLoginUri;
      }
    } else if (planId === 'pro') {
      track.onClick({
        clickEventName: ClickEventName.click_landing_plan_card_pro_subscribe,
        params: {
          is_logged_in: accessToken ? 'true' : 'false',
          use_months: billingPeriod === 'monthly' ? '1' : '12'
        }
      });
      if (accessToken) {
        history.push(`/checkout/pro/select-channel?billingPeriod=${billingPeriod}`);
      } else {
        const loginUrl = `${window.location.origin}/auth/login?redirect=/checkout/pro/select-channel?billingPeriod=${billingPeriod}`;
        const oauthLoginUri = `https://${OAUTH_LOGIN_HOST}/auth/signin?redirectUrl=${encodeURIComponent(
          loginUrl
        )}&referrer=studio`;
        window.location.href = oauthLoginUri;
      }
    } else if (planId === 'enterprise') {
      track.onClick({
        clickEventName: ClickEventName.click_landing_plan_card_enterprise_contact,
        params: { is_logged_in: accessToken ? 'true' : 'false' }
      });
      window.open('/contact', '_blank', 'noopener,noreferrer');
    } else if (planId === 'class') {
      track.onClick({
        clickEventName: ClickEventName.click_landing_plan_card_class_detail,
        params: { is_logged_in: accessToken ? 'true' : 'false' }
      });
      window.open(
        'https://realworld-school.org/theme/realworld/subscribe/',
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  return (
    <PageContainer>
      <SharedNavBar />

      <HeaderSection ref={headerAnimation.ref} isVisible={headerAnimation.isVisible}>
        <Title>{header.title}</Title>
        <Subtitle>{header.subtitle}</Subtitle>
        <PricingToggle selected={billingPeriod} onChange={setBillingPeriod} />
      </HeaderSection>

      <PlansSection ref={plansAnimation.ref} isVisible={plansAnimation.isVisible}>
        <PlansGrid>
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              billingPeriod={billingPeriod}
              onCtaClick={handleCtaClick}
            />
          ))}
        </PlansGrid>
      </PlansSection>

      <div>
        <Typography
          type="caption"
          css={css`
            color: #adadad;
          `}
        >
          *모든 유료 요금제는 부가가치세(VAT)가 포함된 가격입니다.
        </Typography>
      </div>

      {!showComparison && (
        <ComparisonToggleWrapper
          ref={comparisonAnimation.ref}
          isVisible={comparisonAnimation.isVisible}
        >
          <ComparisonToggle
            isOpen={showComparison}
            onClick={() => {
              track.onClick({ clickEventName: ClickEventName.click_landing_plan_compare_detail });
              setShowComparison(!showComparison);
            }}
          />
        </ComparisonToggleWrapper>
      )}

      {showComparison && (
        <ComparisonTable sections={comparison.sections} onClose={() => setShowComparison(false)} />
      )}

      <CasesSection />

      <FAQWrapper ref={faqAnimation.ref} isVisible={faqAnimation.isVisible}>
        <FAQSection title={faq.title} items={faq.items} />
      </FAQWrapper>

      <CTAWrapper ref={ctaAnimation.ref} isVisible={ctaAnimation.isVisible}>
        <CTASection title={cta.title} subtitle={cta.subtitle} buttons={cta.buttons} />
      </CTAWrapper>

      <Footer />
    </PageContainer>
  );
};

export default Pricing;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  min-width: 100%;
  background: #f5f5f5;
  align-items: center;
`;

const HeaderSection = styled.section<{ isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 150px 0 0px 0px;
  gap: ${spacing.common.xlarge};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: ${typography.weight.bold};
  color: ${color.text_default};
  text-align: center;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: ${typography.size.s3};
  color: ${color.text_default};
  text-align: center;
  margin: 0;
  white-space: pre-line;
  line-height: 1.6;
`;

const PlansSection = styled.section<{ isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${spacing.padding.xlarge3} ${spacing.padding.xlarge2};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: opacity 0.6s ease-out 0.15s, transform 0.6s ease-out 0.15s;
`;

const PlansGrid = styled.div`
  display: flex;
  gap: 16px;
  margin: 0 auto;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ComparisonToggleWrapper = styled.section<{ isVisible: boolean }>`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: opacity 0.6s ease-out 0.3s, transform 0.6s ease-out 0.3s;
`;

const FAQWrapper = styled.section<{ isVisible: boolean }>`
  width: 100%;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: opacity 0.6s ease-out 0.45s, transform 0.6s ease-out 0.45s;
`;

const CTAWrapper = styled.section<{ isVisible: boolean }>`
  width: 100%;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: opacity 0.6s ease-out 0.6s, transform 0.6s ease-out 0.6s;
`;
